import type { Filter } from "@1js/pl-card-components";
import type { Group, Prompt } from "@1js/pl-types";
import { useMsal } from "@azure/msal-react";
import React from "react";
import { useFlightContext } from "../../hooks/useFlightContext";
import { useHandoffService } from "../../hooks/useHandoffService";
import { MetaOSAppContext } from "../../pages/metaos/MetaOSAppContext";
import { LibraryContext } from "../../pages/prompts/allPromptsLibrary/LibraryContext";
import { deleteSharedPromptFromWorkgroup } from "../../services/prompt-client.web";
import useTelemetry from "../../utils/Telemetry/useTelemetry";
import type { HandlePromptsTabSelected, Notify } from "./AllPrompts.web.data";
import { useWorkgroups } from "./useWorkgroups.hook";

export type UseWorkgroupPromptsProps = {
  notify: Notify;
  promptRemovedFromTeamToastErrorTitle: () => string;
  promptRemovedFromTeamToastTitle: () => string;
  handlePromptsTabSelected: HandlePromptsTabSelected;
  setDialogOpen: (isOpen: boolean) => void;
  promptRefs: React.MutableRefObject<
    Map<string, React.RefObject<HTMLDivElement>>
  >;
  setCurrentFocusedPromptId: (promptId: string) => void;
  onGroupPromptsFilterChanged: (
    filter: Record<string, string[]>,
    groups?: Group[]
  ) => void;
};

const PromptRemovalProgressWaitTime = 1000;

export const useWorkgroupPrompts = ({
  notify,
  promptRemovedFromTeamToastErrorTitle,
  promptRemovedFromTeamToastTitle,
  handlePromptsTabSelected,
  setDialogOpen,
  promptRefs,
  setCurrentFocusedPromptId,
  onGroupPromptsFilterChanged,
}: UseWorkgroupPromptsProps) => {
  useWorkgroups({ handlePromptsTabSelected, isRunningOnWorkgroupTab: true });
  const telemetry = useTelemetry();
  const { isFlightActive } = useFlightContext();
  const { chooseBestProductForHandoff } = useHandoffService();
  const { state, dispatch } = React.useContext(LibraryContext);
  const [removeDialogConfig, setRemoveDialogConfig] = React.useState<{
    isOpen: boolean;
    prompts: Prompt[] | undefined;
    groupIds: string[] | undefined;
  }>({
    isOpen: false,
    prompts: undefined,
    groupIds: undefined,
  });

  const metaOSAppContext = React.useContext(MetaOSAppContext);
  const msalInstance = useMsal();
  const instance =
    metaOSAppContext.isMosApp && metaOSAppContext.pca
      ? msalInstance.instance
      : undefined;

  const handleFilterChanged = React.useCallback(
    (selectedValues: Record<string, string[]>, _: Filter[]) => {
      onGroupPromptsFilterChanged(selectedValues);
    },
    [onGroupPromptsFilterChanged]
  );

  const handleOnRemoveFromWorkgroupClicked = React.useCallback(
    (prompts: Prompt[], groupIds: string[]) => {
      telemetry.sendPromptActionEvent(
        "WorkgroupDelete",
        "PromptAction",
        "RemoveButton",
        prompts[0],
        false
      );
      setDialogOpen(false);
      setRemoveDialogConfig({
        isOpen: true,
        prompts,
        groupIds,
      });
    },
    [telemetry, setDialogOpen]
  );

  const focusOnFirstAvailablePrompt = React.useCallback(
    (prompts: Prompt[]) => {
      for (let promptIndex = 0; promptIndex < prompts.length; promptIndex++) {
        if (promptRefs.current.has(prompts[promptIndex].Id)) {
          setCurrentFocusedPromptId(prompts[promptIndex].Id);

          promptRefs.current.get(prompts[promptIndex].Id)?.current?.focus();
          break;
        }
      }
    },
    [promptRefs, setCurrentFocusedPromptId]
  );

  const focusOnNextPrompt = React.useCallback(
    (prompt: Prompt) => {
      const promptId = prompt.Id;
      const currentCard = promptRefs.current.get(promptId);
      const parentElement = currentCard?.current?.parentElement;
      const nextCard = parentElement?.nextElementSibling?.firstElementChild;
      const previousCard =
        parentElement?.previousElementSibling?.firstElementChild;

      if (nextCard) {
        (nextCard as HTMLElement).focus();
        (nextCard as HTMLElement).tabIndex = 0;
      } else if (previousCard) {
        (previousCard as HTMLElement).focus();
        (previousCard as HTMLElement).tabIndex = 0;
      }
    },
    [promptRefs]
  );

  const handleOnRemoveClose = React.useCallback(
    (isClosedByCustomer: boolean) => {
      if (isClosedByCustomer) {
        telemetry.sendPromptActionEvent(
          "WorkgroupView",
          "Dialog",
          "CancelButton",
          removeDialogConfig.prompts?.[0] as Prompt
        );
      }

      if (removeDialogConfig.prompts) {
        focusOnFirstAvailablePrompt(removeDialogConfig.prompts);
      }

      setRemoveDialogConfig({
        ...removeDialogConfig,
        isOpen: false,
      });
    },
    [focusOnFirstAvailablePrompt, removeDialogConfig, telemetry]
  );

  const serviceHost = state.serviceHost;
  const handleOnRemoveFromWorkgroupRemoveClicked = React.useCallback(
    async (prompts: Prompt[], groupIds: string[]) => {
      telemetry.sendPromptActionEvent(
        "WorkgroupDelete",
        "Dialog",
        "RemoveButton",
        prompts[0],
        false
      );
      try {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        focusOnFirstAvailablePrompt(removeDialogConfig.prompts!);

        dispatch({
          type: "REMOVE_GROUP_PROMPT_INIT",
          payload: { prompts, groupIds },
        });

        setTimeout(() => {
          // dispatch could be undefined if the component has been unmounted already
          dispatch?.({
            type: "REMOVE_GROUP_PROMPT_LONG",
            payload: { prompts, groupIds },
          });
        }, PromptRemovalProgressWaitTime);
        focusOnNextPrompt(prompts[0]);

        const deletedPrompts = await deleteSharedPromptFromWorkgroup(
          serviceHost,
          prompts,
          groupIds,
          telemetry.sendApiActionEvent,
          instance
        );
        if (deletedPrompts.Prompts.length !== prompts.length) {
          notify("error", promptRemovedFromTeamToastErrorTitle());
          return;
        }

        notify(
          "success",
          promptRemovedFromTeamToastTitle(),
          undefined,
          undefined
        );
        dispatch({
          type: "REMOVE_GROUP_PROMPT_SUCCESS",
          payload: {
            groupIds,
            promptsToBeDeleted: prompts,
            deletedPrompts: deletedPrompts.Prompts,
            isUnifiedWorkgroupPromptsViewEnabled: isFlightActive(
              "isUnifiedWorkgroupPromptsViewEnabled"
            ),
          },
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        notify("error", promptRemovedFromTeamToastErrorTitle());
      }
    },
    [
      dispatch,
      focusOnFirstAvailablePrompt,
      focusOnNextPrompt,
      instance,
      isFlightActive,
      notify,
      promptRemovedFromTeamToastErrorTitle,
      promptRemovedFromTeamToastTitle,
      removeDialogConfig.prompts,
      serviceHost,
      telemetry,
    ]
  );

  const handleOnRemoveDialogClicked = React.useCallback(() => {
    !!removeDialogConfig.prompts &&
      !!removeDialogConfig.groupIds &&
      handleOnRemoveFromWorkgroupRemoveClicked(
        removeDialogConfig.prompts,
        removeDialogConfig.groupIds
      );
    handleOnRemoveClose(false);
  }, [
    handleOnRemoveClose,
    handleOnRemoveFromWorkgroupRemoveClicked,
    removeDialogConfig.groupIds,
    removeDialogConfig.prompts,
  ]);

  return {
    handleOnRemoveClose,
    handleOnRemoveDialogClicked,
    handleOnRemoveFromWorkgroupClicked,
    removeDialogConfig,
    isFlightActive,
    handleFilterChanged,
    chooseBestProductForHandoff,
  };
};
